import React, { useState, useEffect, createContext, useContext } from 'react';
import { Box, List, ListItem, ListItemIcon, Tooltip, Typography } from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// Kontekst do zarządzania stanem tooltipów i aktywnym obszarem
interface SidebarContextType {
  showTooltips: boolean;
  activeSection: string | null;
  setActiveSection: (section: string | null) => void;
}

// Interfejs dla bloków funkcjonalnych
interface FunctionalBlock {
  element: HTMLElement;
  id: string;
  tabSelector: string;
}

export const SidebarContext = createContext<SidebarContextType>({
  showTooltips: false,
  activeSection: null,
  setActiveSection: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

// Właściwości przenoszone do głównego komponentu Sidebar
interface SidebarProps {
  children?: React.ReactNode;
}

const Sidebar: React.FC<SidebarProps> = ({ children }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [visible, setVisible] = useState(() => {
    try { return (localStorage.getItem('ui:sidebar:visible') ?? '0') === '1'; } catch { return false; }
  });
  const [showTooltips, setShowTooltips] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Sprawdzanie rozmiaru ekranu do trybu responsywnego
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reakcja na przełącznik w Ustawieniach → Widok
  useEffect(() => {
    const onToggle = (e: any) => {
      if (e?.detail && typeof e.detail.visible === 'boolean') setVisible(e.detail.visible);
    };
    window.addEventListener('ui:sidebar:toggle', onToggle as any);
    return () => window.removeEventListener('ui:sidebar:toggle', onToggle as any);
  }, []);

  // Wykrywanie systemu operacyjnego
  const [isMac, setIsMac] = useState(false);
  useEffect(() => {
    // Sprawdzenie czy użytkownik używa macOS
    setIsMac(navigator.platform.toUpperCase().indexOf('MAC') >= 0);
  }, []);

  // Określenie odpowiedniego skrótu w zależności od systemu
  const getShortcutText = (index: number) => {
    return isMac ? `Option + ${index}` : `Alt + ${index}`;
  };

  const can = (key: string) => {
    if (user?.role && String(user.role).toLowerCase() === 'admin') return true;
    // Jeśli brak mapy lub jest pusta → nie blokujemy (wsteczna kompatybilność)
    if (!user?.permissions || Object.keys(user.permissions).length === 0) return true;
    // Specjalny fallback: jeśli nie ma klucza invoices, pokaż moduł faktur
    if (key === 'invoices' && !(key in user.permissions)) return true;
    return !!user.permissions[key as keyof typeof user.permissions];
  };

  const canAnyApp = (): boolean => {
    if (user?.role && String(user.role).toLowerCase() === 'admin') return true;
    const perms = user?.permissions;
    if (!perms || Object.keys(perms).length === 0) return true;
    const appKeys = ['estimates','materials_approvals','schedules','maps','requester','human_resources','contracts'];
    return appKeys.some(k => !!(perms as any)[k]);
  };

  const appRootUrl = React.useMemo(() => {
    const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    return isLocalhost ? 'http://localhost:3000/app' : 'https://app.unitresco.com/app';
  }, []);

  const menuItems = [
    can('home') && { text: 'Strona główna', iconKey: 'home', path: '/', shortcut: getShortcutText(1) },
    can('projects') && { text: 'Projekty', iconKey: 'projects', path: '/projects', shortcut: getShortcutText(2) },
    can('offers') && { text: 'Oferty', iconKey: 'offers', path: '/offers', shortcut: getShortcutText(3) },
    can('database') && { text: 'Bazy danych', iconKey: 'database', path: '/database', shortcut: getShortcutText(4) },
    canAnyApp() && { text: 'Aplikacje', iconKey: 'apps', path: appRootUrl, shortcut: getShortcutText(5), external: true },
    can('invoices') && { text: 'Faktury', iconKey: 'invoices', path: '/accounting/purchases', shortcut: getShortcutText(6) },
    can('service') && { text: 'Serwis', iconKey: 'service', path: '/service', shortcut: getShortcutText(8) },
    { text: 'Zgłoś błąd', iconKey: 'bug', path: '/bug-report', shortcut: getShortcutText(9) },
    can('permissions') && { text: 'Ustawienia', iconKey: 'settings', path: '/settings', shortcut: getShortcutText(9) },
    can('help') && { text: 'Pomoc', iconKey: 'help', path: '/help', shortcut: getShortcutText(9) },
  ].filter(Boolean) as Array<{ text:string; iconKey:string; path:string; shortcut:string; external?: boolean }>;

  const getIconSrc = (key: string): string => {
    switch (key) {
      case 'home': return '/assets/sidebar/home.svg';
      case 'projects': return '/assets/sidebar/projects.svg';
      case 'offers': return '/assets/sidebar/offers.svg';
      case 'database': return '/assets/sidebar/database.svg';
      case 'apps': return '/assets/sidebar/apps.svg';
      case 'invoices': return '/assets/sidebar/invoices.svg';
      case 'service': return '/assets/sidebar/service.svg';
      case 'bug': return '/assets/sidebar/bug.svg';
      case 'settings': return '/assets/sidebar/settings.svg';
      case 'help': return '/assets/sidebar/help.svg';
      default: return '/assets/sidebar/home.svg';
    }
  };

  // Wyłączone podpowiadanie numerów skrótów przy wciśniętym Alt/Option
  // Skróty klawiszowe Alt+1..9 pozostają obsługiwane w innym efekcie poniżej.

  // Wykrywanie aktywnego obszaru ale bez zaznaczania wizualnego
  useEffect(() => {
    
    // Funkcja do znajdowania głównych bloków funkcjonalnych
    const findFunctionalBlocks = (): FunctionalBlock[] => {
      const blocks: FunctionalBlock[] = [];
      
      // UPPER BLOCK - obszar z filtrami, kartami i tabelą projektów
      // Znajdź kontener z tabelą projektów i wejdź w górę, żeby znaleźć główny kontener
      const projectTable = document.querySelector('.MuiTableContainer-root');
      if (projectTable) {
        // Idź w górę drzewa DOM, żeby znaleźć kontener obejmujący filtry + karty + tabelę
        let container = projectTable.parentElement;
        while (container && container !== document.body) {
          // Sprawdź czy ten kontener zawiera również filtry/wyszukiwanie
          const hasSearchInput = container.querySelector('input[placeholder*="Szukaj"]');
          const hasFilterTabs = container.querySelector('.MuiTabs-root');
          
          if (hasSearchInput || hasFilterTabs) {
            // To jest prawdopodobnie główny kontener upper block
            blocks.push({
              element: container as HTMLElement,
              id: 'upper-block',
              tabSelector: '.MuiTabs-root .MuiTab-root'
            });
            break;
          }
          container = container.parentElement;
        }
      }
      
      // LOWER BLOCK - obszar ze szczegółami projektu i kartami
      // Znajdź kontener z nazwą projektu i kartami szczegółów
      const projectDetailsSection = document.querySelector('[role="tablist"]');
      if (projectDetailsSection) {
        // Idź w górę, żeby znaleźć główny kontener lower block
        let container = projectDetailsSection.parentElement;
        while (container && container !== document.body) {
          // Sprawdź czy nie jest to już część upper block
          const isPartOfUpperBlock = blocks.some(block => 
            block.id === 'upper-block' && block.element.contains(container)
          );
          
          if (!isPartOfUpperBlock) {
            // Sprawdź czy zawiera karty szczegółów projektu
            const hasProjectTabs = container.querySelector('[role="tablist"]');
            const hasProjectTitle = container.querySelector('h1, h2, h3, h4, h5, h6') || 
                                   container.textContent?.includes('PROJEKT') ||
                                   container.textContent?.includes('WARSZAWA') ||
                                   container.textContent?.includes('KONSTRUKC') ||
                                   container.textContent?.includes('KOSTKI');
            
            if (hasProjectTabs) {
              blocks.push({
                element: container as HTMLElement,
                id: 'lower-block',
                tabSelector: '[role="tablist"] [role="tab"]'
              });
              break;
            }
          }
          container = container.parentElement;
        }
      }
      
      return blocks;
    };

    // Funkcja do obsługi zdarzenia najechania kursorem (bez zaznaczania wizualnego)
    const handleMouseOver = (event: MouseEvent) => {
      const blocks = findFunctionalBlocks();
      const target = event.target as Element;
      
      let hoveredBlock: FunctionalBlock | null = null;
      
      // Sprawdź, w którym bloku nastąpiło najechanie
      for (const block of blocks) {
        if (block.element.contains(target)) {
          hoveredBlock = block;
          break;
        }
      }

      // Ustawienie aktywnego bloku bez zaznaczania wizualnego
      if (hoveredBlock) {
        setActiveSection(hoveredBlock.id);
        console.log(`Aktywny blok funkcjonalny: ${hoveredBlock.id}`);
      } else {
        setActiveSection(null);
      }
    };

    document.addEventListener('mouseover', handleMouseOver);
    
    return () => {
      document.removeEventListener('mouseover', handleMouseOver);
    };
  }, []);

  // Implementacja skrótów klawiszowych (działa także, gdy sidebar jest ukryty)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Obsługa skrótów Alt+1 do Alt+9
      if (event.altKey && !event.metaKey) {
        // Sprawdzamy kody klawiszy 1-9 (KeyCode 49-57)
        if (event.keyCode >= 49 && event.keyCode <= 57) {
          event.preventDefault();
          // Konwertujemy keyCode na index (49 -> 1, 50 -> 2, itd.)
          const index = event.keyCode - 49;
          if (index < menuItems.length) {
            const item = menuItems[index] as any;
            console.log(`Skrót klawiszowy: Alt + ${index + 1}, kierowanie do: ${item.path}`);
            if (item.external) {
              window.location.href = item.path;
            } else {
              navigate(item.path);
            }
          }
        }
      }

      // Nowy skrót: klawisz "f" przenosi fokus do pola wyszukiwania w określonych modułach
      if (event.key === 'f' && !event.altKey && !event.ctrlKey && !event.metaKey) {
        // Sprawdź, czy jesteśmy w jednym z modułów z wyszukiwaniem
        const path = location.pathname;
        
        // Sprawdź, czy aktualnie aktywny element to pole tekstowe, input lub textarea
        const activeTag = document.activeElement?.tagName?.toLowerCase();
        const isInTextField = activeTag === 'input' || activeTag === 'textarea' || 
                             (document.activeElement as HTMLElement)?.isContentEditable;
        
        // Nie uruchamiaj skrótu jeśli już jesteśmy w polu tekstowym
        if (isInTextField) {
          return;
        }
        
        // Blok try-catch aby uniknąć błędów jeśli element nie zostanie znaleziony
        try {
          let searchInput = null;
          
          if (path.includes('/projects')) {
            searchInput = document.querySelector('input[placeholder*="Szukaj projekt"]');
          } else if (path.includes('/offers')) {
            searchInput = document.querySelector('input[placeholder*="Szukaj ofert"]');
          } else if (path.includes('/database')) {
            searchInput = document.querySelector('input[placeholder*="Szukaj kontrahent"]');
          }
          
          if (searchInput) {
            event.preventDefault(); // Zapobiegaj wpisaniu "f" w pole
            console.log('Skrót klawiszowy: f, przenoszenie fokusa do pola wyszukiwania');
            (searchInput as HTMLElement).focus();
          }
        } catch (error) {
          console.error('Błąd podczas przenoszenia fokusa do pola wyszukiwania:', error);
        }
      }

      // Zakładki tabel: klawisz "`" (backtick) – bez Shift w prawo, z Shift w lewo
      // Uodpornione na różne układy klawiatury: sprawdzamy event.code
      if (
        !event.altKey && !event.ctrlKey && !event.metaKey &&
        (event.key === '`' || event.code === 'Backquote')
      ) {
        event.preventDefault();
        const step = event.shiftKey ? -1 : 1;
        
        // Nie działaj w polach edycyjnych
        const tag = (event.target as HTMLElement)?.tagName?.toLowerCase();
        const isEditable = tag === 'input' || tag === 'textarea' || (event.target as HTMLElement)?.isContentEditable;
        if (isEditable) return;

        // Preferowane: przełączaj w obrębie tej samej listy co aktywna karta (pewne wrap-around)
        try {
          const activeTabEl = document.querySelector('[role="tab"][aria-selected="true"], .MuiTab-root.Mui-selected') as HTMLElement | null;
          if (activeTabEl) {
            const tabList = activeTabEl.closest('[role="tablist"]') || activeTabEl.closest('.MuiTabs-root');
            let tabsArr = tabList ? Array.from(tabList.querySelectorAll('[role="tab"]')) as HTMLElement[] : [activeTabEl];
            tabsArr = tabsArr.filter(el => el.getAttribute('aria-disabled') !== 'true');
            const curIdx = Math.max(0, tabsArr.indexOf(activeTabEl));
            const nextIdx = tabsArr.length === 0 ? 0 : (curIdx + step + tabsArr.length) % tabsArr.length;
            const nextEl = tabsArr[nextIdx] as HTMLElement | undefined;
            if (nextEl) { nextEl.click(); }
            return;
          }
        } catch {}

        if (!activeSection) {
          // Fallback: spróbuj globalnie znaleźć listę kart na stronie
          try {
            const tabsGlobal = document.querySelectorAll('[role="tablist"] [role="tab"]');
            if (!tabsGlobal || tabsGlobal.length === 0) {
              console.log('Brak aktywnego bloku funkcjonalnego');
              return;
            }
            const tabsArr = Array.from(tabsGlobal);
            let activeIdx = tabsArr.findIndex(el => el.classList.contains('Mui-selected') || el.getAttribute('aria-selected') === 'true');
            const nextIdx = tabsArr.length === 0 ? 0 : ((activeIdx < 0 ? 0 : (activeIdx + step + tabsArr.length) % tabsArr.length));
            (tabsArr[nextIdx] as HTMLElement).click();
            return;
          } catch {
            console.log('Brak aktywnego bloku funkcjonalnego');
            return;
          }
        }
        
        try {
          let tabElements: NodeListOf<Element> | null = null;
          
          // Wybierz odpowiednie selektory w zależności od aktywnego bloku
          if (activeSection === 'upper-block') {
            // Górny blok - karty filtrów projektów (W REALIZACJI, ZAKOŃCZONE W OKRESIE GWARANCJI, etc.)
            tabElements = document.querySelectorAll('.MuiTabs-root .MuiTab-root');
          } else if (activeSection === 'lower-block') {
            // Dolny blok - karty szczegółów projektu (Szczegóły, Kontraktowanie, Materiały, etc.)
            tabElements = document.querySelectorAll('[role="tablist"] [role="tab"]');
          }
          
          if (!tabElements || tabElements.length === 0) {
            console.log('Nie znaleziono kart w aktywnym bloku');
            return;
          }
          
          // Znajdź aktywną kartę
          let activeIndex = -1;
          // Konwersja NodeList na Array aby uniknąć błędu TypeScript
          const tabElementsArray = Array.from(tabElements);
          for (let i = 0; i < tabElementsArray.length; i++) {
            if (tabElementsArray[i].classList.contains('Mui-selected') || 
                tabElementsArray[i].getAttribute('aria-selected') === 'true') {
              activeIndex = i;
              break;
            }
          }
          
          // Przejdź do następnej/poprzedniej karty z zawijaniem
          const nextIndex = tabElementsArray.length === 0
            ? 0
            : ((activeIndex < 0 ? 0 : (activeIndex + step + tabElementsArray.length) % tabElementsArray.length));
          
          console.log(`Przełączam z karty ${activeIndex} na kartę ${nextIndex} w bloku ${activeSection}`);
          (tabElements[nextIndex] as HTMLElement).click();
        } catch (error) {
          console.error('Błąd podczas przełączania kart:', error);
        }
      }

      // Zakładki główne: klawisz "§" (oraz warianty na różnych układach: £/±) – bez Shift w prawo, z Shift w lewo
      if (
        !event.altKey && !event.ctrlKey && !event.metaKey &&
        (event.code === 'IntlBackslash' || event.key === '§' || event.key === '£' || event.key === '±')
      ) {
        event.preventDefault();
        // Nie działaj w polach edycyjnych
        const tag = (event.target as HTMLElement)?.tagName?.toLowerCase();
        const isEditable = tag === 'input' || tag === 'textarea' || (event.target as HTMLElement)?.isContentEditable;
        if (isEditable) return;
        const step = event.shiftKey ? -1 : 1;
        try {
          const container = document.querySelector('[data-tab-scope="main"]') as HTMLElement | null;
          if (!container) return;
          const tabNodes = Array.from(container.querySelectorAll('[data-tab="main"]')) as HTMLElement[];
          if (tabNodes.length === 0) return;
          let activeIdx = tabNodes.findIndex(el => el.getAttribute('aria-selected') === 'true');
          if (activeIdx < 0) activeIdx = 0;
          const nextIdx = (activeIdx + step + tabNodes.length) % tabNodes.length;
          tabNodes[nextIdx].click();
        } catch (e) {
          console.error('Błąd podczas przełączania zakładek głównych:', e);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate, menuItems, location.pathname, activeSection]);

  // Zawartość sidebara - stała szerokość 60px
  if (!visible) {
    return (
      <SidebarContext.Provider value={{ showTooltips, activeSection, setActiveSection }}>
        {children}
      </SidebarContext.Provider>
    );
  }

  const sidebarContent = (
    <Box
      sx={{
        width: 40,
        backgroundColor: '#020242',
        borderRight: 'none',
        height: 'calc(100vh - 52px - var(--legacy-host-banner-height, 0px))',
        position: 'fixed',
        left: 0,
        top: 'calc(52px + var(--legacy-host-banner-height, 0px))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        color: '#fff',
        zIndex: 5,
        overflowX: 'hidden',
      }}
    >
      <List sx={{ width: '100%', pt: 1 }}>
        {menuItems.map((item, index) => {
          const isHome = item.path === '/';
          const isApps = item.iconKey === 'apps';
          const isAccounting = item.iconKey === 'invoices';
          const isActive = isHome
            ? (location.pathname === '/')
            : isApps
              ? location.pathname.startsWith('/app')
              : isAccounting
                ? location.pathname.startsWith('/accounting')
                : location.pathname.startsWith(item.path);
          return (
          <Box key={index} sx={{ position: 'relative' }}>
            {/* Zwykły tooltip pokazujący nazwę po najechaniu */}
            <Tooltip 
              title={item.text} 
              placement="right"
              arrow
              PopperProps={{
                sx: {
                  '& .MuiTooltip-tooltip': {
                    backgroundColor: '#ffffff',
                    border: '1px solid #e0e0e0',
                    fontSize: '0.75rem',
                    maxWidth: 'none',
                    padding: '6px 8px',
                    color: 'rgba(0,0,0,0.8)'
                  }
                }
              }}
            >
              <ListItem
                button
                component={item.external ? 'a' as any : Link}
                {...(item.external ? { href: item.path } : { to: item.path })}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 1.5,
                  px: 0,
                  height: '40px',
                  backgroundColor: isActive ? '#2020AF' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  },
                }}
              >
                <ListItemIcon sx={{ 
                  color: '#ffffff', 
                  minWidth: 'auto', 
                  mb: 0,
                  fontSize: '0.9rem',
                  padding: '8px',
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.1rem',
                  }
                }}>
                  <Box
                    aria-hidden
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: '#D4D4D9',
                      WebkitMaskImage: `url(${getIconSrc(item.iconKey)})`,
                      maskImage: `url(${getIconSrc(item.iconKey)})`,
                      WebkitMaskRepeat: 'no-repeat',
                      maskRepeat: 'no-repeat',
                      WebkitMaskPosition: 'center',
                      maskPosition: 'center',
                      WebkitMaskSize: 'contain',
                      maskSize: 'contain'
                    }}
                  />
                </ListItemIcon>
              </ListItem>
            </Tooltip>
            
            {/* Numer skrótu klawiaturowego - widoczny tylko po naciśnięciu Alt/Option */}
            {false && showTooltips && (
              <Typography 
                variant="caption" 
                sx={{
                  position: 'absolute',
                  right: 3,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: '#ffffff',
                  border: '1px solid #d0d0d0',
                  color: 'text.secondary',
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10
                }}
              >
                {index + 1}
              </Typography>
            )}
          </Box>
        );})}
      </List>
      
      {/* Instrukcja skrótów klawiszowych */}
      <Box 
        sx={{ 
          position: 'absolute', 
          bottom: 10, 
          left: 0, 
          width: '100%', 
          textAlign: 'center',
          fontSize: '0.6rem',
          color: 'text.secondary',
          px: 0.5
        }}
      >
        {showTooltips && (
          <>
            <Typography variant="caption" sx={{ display: 'block', fontSize: 'inherit' }}>
              f = szukaj
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', fontSize: 'inherit' }}>
              ` = karty
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <SidebarContext.Provider value={{ showTooltips, activeSection, setActiveSection }}>
      {sidebarContent}
      {children}
    </SidebarContext.Provider>
  );
};

// Hook dla komponentów potrzebujących informacji o aktywnym obszarze
export const useActiveSection = () => {
  const { activeSection, setActiveSection } = useContext(SidebarContext);
  return { activeSection, setActiveSection };
};

// Komponent ramki dla obszarów kart
export const TabAreaOutline: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => {
  const { activeSection } = useActiveSection();
  
  return (
    <div 
      className={`tab-area ${id}`}
      style={{ 
        position: 'relative'
      }}
    >
      {children}
    </div>
  );
};

export default Sidebar;
